/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: 品类表格组件
 */
import React, { useRef, useMemo, useState, useEffect, useImperativeHandle } from 'react';
import axios from 'axios';
import classnames from 'classnames';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import CardSlotV2 from '@intelligence/components/CardSlotV2';
import { DBTable } from '@home/components/TeaComponents';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import { fetchCasualGenreProfileTableData } from '../../services';
import { isEmpty } from '@intelligence/v3/utils/common';
import { buildColumns, IBuildColumnsOptions } from './columns';

import type { PagingQuery } from 'tea-component';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';
import type {
  IGenreTableModuleInstance,
  IGenreTableModuleUpdateParams,
  TDataSourceType,
  TMetricType,
  TDimension,
  TTableSegment,
} from '../../types';

import './index.scss';

interface IGenreTableProps extends IBaseProps {
  /** 实体类型 (用于表格图片组件) */
  entityType: IEntityType;
  /** 当前日期粒度 */
  granularity?: IGranularity;
  /** 品类选项数据 map */
  genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  /** 品类数据源 */
  genreSource: IGenreSource;
  /** 当前数据源 */
  dataSource: TDataSourceType;
  /** 当前指标 */
  metric: TMetricType;
  /** 当前维度 */
  dimension: TDimension;
  /** 表格Segment值 */
  tableSegment: TTableSegment;
  sorts: IRatioMetricSortState<ICasualGenreProfileMetricData>[];
  onSortsChange: any;
  /** 分页状态 */
  pagingQuery: PagingQuery;
  /** 分页变更回调 */
  onPagingChange: (query: PagingQuery) => void;
  /** More按钮点击回调 */
  onMoreGamesClick?: (columnKey: string, record: ICasualGenreProfileMetricData) => void;
  /** 搜索关键字 */
  keyword?: string;
}

/**
 * 获取记录的唯一标识
 */
const getRecordKey = (record: ICasualGenreProfileMetricData): string => {
  return record?.game_id || '';
};

/**
 * GenreTable 品类表格组件
 */
const GenreTable: React.ForwardRefRenderFunction<IGenreTableModuleInstance, IGenreTableProps> = (props, ref) => {
  const {
    style,
    className,
    entityType,
    granularity = 'monthly',
    genreOptionsMap,
    genreSource,
    dataSource,
    metric,
    dimension,
    tableSegment,
    sorts,
    onSortsChange,
    pagingQuery,
    onPagingChange,
    onMoreGamesClick,
    keyword,
  } = props;

  const { locale, formatMessage } = useIntlExtend();

  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [tableList, setTableList] = useState<ICasualGenreProfileMetricData[]>([]);
  const [entityInfoMap, setEntityInfoMap] = useState<IRecord>({});

  // 表格容器 ref（用于锚点滚动）
  const tableRef = useRef<HTMLDivElement>(null);

  // 用于取消之前的请求（处理竞态问题）
  const cancelTokenSourceRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(null);

  // 组件卸载时取消请求
  useEffect(() => {
    return () => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Component unmounted');
      }
    };
  }, []);

  const noData = isEmpty(tableList);

  /**
   * 滚动到指定列
   * @param columnKey 列的 key 值
   */
  const scrollToColumn = (columnKey: string): void => {
    if (!tableRef.current) return;

    const columnEls = tableRef.current.querySelectorAll('.tea-table__body col');
    const body = tableRef.current.querySelector('.tea-table__body');

    if (!columnEls.length || !body) return;

    // 构建当前列配置
    const columns = buildColumns({
      locale,
      entityType,
      granularity,
      genreOptionsMap,
      genreSource,
      dataSource,
      metric,
      dimension,
      tableSegment,
      sorts,
      onSortsChange,
      entityInfoMap,
      onMoreGamesClick,
      keyword,
    } as IBuildColumnsOptions);

    const pos = columns.findIndex((item) => item.key === columnKey);
    if (pos <= 0) return;

    // 累加前面非固定列的宽度
    let width = 0;
    for (let i = 0; i < pos; i++) {
      const col = columnEls[i];
      if (col && !columns[i].fixed) {
        const w = Number.parseFloat(col?.style?.width) || 0;
        width += w;
      }
    }

    body.scrollTo({
      left: width,
      behavior: 'smooth',
    });
  };

  /**
   * 表格列配置
   */
  const columns = useMemo(() => {
    return buildColumns({
      locale,
      entityType,
      granularity,
      genreOptionsMap,
      genreSource,
      dataSource,
      metric,
      dimension,
      tableSegment,
      sorts,
      onSortsChange,
      entityInfoMap,
      onMoreGamesClick,
      keyword,
    });
  }, [
    locale,
    entityType,
    granularity,
    genreOptionsMap,
    genreSource,
    dataSource,
    metric,
    dimension,
    tableSegment,
    keyword,
    entityInfoMap,
    onMoreGamesClick,
  ]);

  const update = async (params: IGenreTableModuleUpdateParams) => {
    const {
      genreSource,
      genreDimension,
      dataSource,
      metric,
      filterParams,
      sorts,
      pagingQuery,
      keyword,
      genreKeywords,
    } = params || {};

    // 取消之前的请求（处理竞态问题）
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel('Request canceled due to new search');
    }

    // 创建新的 CancelToken
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    setLoading(true);
    try {
      const res = await fetchCasualGenreProfileTableData({
        genreSource,
        genreDimension,
        dataSource,
        metric,
        filterParams,
        sorts,
        pagingQuery,
        keyword: keyword || '',
        genreKeywords: genreKeywords || [],
        cancelToken: cancelTokenSource.token,
      });
      setTotal(res?.data?.total ?? 0);
      setTableList(res?.data?.list || []);
      setEntityInfoMap(res?.ext?.entity_info || {});
    } catch (error) {
      // 如果是主动取消的请求，不处理错误
      if (axios.isCancel(error)) {
        console.log('Request canceled:', (error as any)?.message);
        return;
      }
      console.error('fetchCasualGenreProfileTableData error:', error);
      setTotal(0);
      setTableList([]);
      setEntityInfoMap({});
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    update: (params: IGenreTableModuleUpdateParams) => {
      void update(params);
    },
    scrollToColumn,
  }));

  return (
    <CardSlotV2 ref={tableRef} style={style} className={classnames('dbi-genre-table', className)}>
      {loading && (
        <div className="dbi-genre-table__loading-wrapper">
          <Loading content={formatMessage({ id: 'common_loading' })} />
        </div>
      )}
      {!loading && noData && (
        <div className="dbi-genre-table__nodata-wrapper">
          <NoData content={formatMessage({ id: 'common_nodata' })} />
        </div>
      )}
      {!loading && !noData && (
        <DBTable
          className="db-table--has-check-sequence"
          bordered="all"
          loading={loading}
          recordKey={getRecordKey}
          columns={columns}
          records={tableList}
          total={total}
          pagingQuery={pagingQuery}
          onPagingChange={onPagingChange}
        />
      )}
    </CardSlotV2>
  );
};

// @ts-ignore
export default React.forwardRef(GenreTable);
